/**
 * VRG-690 — the pure half of Analytics → Website's Page analysis table and Site flow chart.
 * NO IMPORTS on purpose (like websiteAnalyticsModel.ts), so
 * `test/website-pages-flows.guard.spec.ts` loads it under node's runner.
 *
 * The published-page list is passed IN (the caller hands it TrackedLinks/destinations.ts,
 * the platform's mirror of vera-website's PUBLIC_ROUTES) rather than imported, which keeps
 * this module import-free and lets the guard feed it the real list.
 */

/** Structural shapes of the API rows this module reads (services/websiteAnalyticsApi.ts). */
export interface PageRowInput {
  path: string
  visitors: number | null
  pageviews: number | null
  leads: number | null
  samplePageviews: number | null
  sampleVisitors: number | null
  avgSeconds: number | null
  timedViews: number
  entries: number | null
  exits: number | null
  exitRate: number | null
}

export interface PublicPage {
  /** A vera.inc path, e.g. "/pricing". */
  value: string
  label: string
}

export interface PageRow extends PageRowInput {
  /** The published page's name, or a description of what a non-page row is. */
  title: string | null
  /** In the published list (vera-website PUBLIC_ROUTES). */
  published: boolean
}

/** Vercel's tail bucket, and a pageview with no path (API: website-pages-flows.ts). */
export const VERCEL_OTHERS = 'Others'
export const UNKNOWN_PATH = '(unknown)'

/** Same normalisation as the API applies, for the published list's own paths. */
const norm = (p: string): string => {
  const s = p.trim().toLowerCase().split(/[?#]/)[0] ?? ''
  const out = `/${s.split('/').filter(Boolean).join('/')}`
  return out
}

export const publishedTitles = (pages: readonly PublicPage[]): Map<string, string> =>
  new Map(pages.filter((p) => p.value.startsWith('/')).map((p) => [norm(p.value), p.label]))

/** What a row that is not a published page IS, in words. Null for an ordinary path. */
const otherTitle = (path: string): string | null => {
  if (path === VERCEL_OTHERS) return 'Outside Vercel’s top 100'
  if (path === UNKNOWN_PATH) return 'No path recorded'
  if (path === '/invite/:code' || path.startsWith('/invite/:code/')) return 'Invite links'
  return null
}

/**
 * Split rows into PUBLISHED pages (the sitemap) and EVERYTHING ELSE (staged pages, old
 * paths, invite links, Vercel's "Others" tail). Order is kept: the API sorts by page views.
 */
export const classifyPages = (
  rows: readonly PageRowInput[],
  titles: ReadonlyMap<string, string>,
): { published: PageRow[]; other: PageRow[] } => {
  const published: PageRow[] = []
  const other: PageRow[] = []
  for (const r of rows) {
    const title = titles.get(r.path)
    if (title !== undefined) published.push({ ...r, title, published: true })
    else other.push({ ...r, title: otherTitle(r.path), published: false })
  }
  return { published, other }
}

const sum = (rows: readonly PageRowInput[], k: keyof PageRowInput): number | null => {
  let any = false
  let total = 0
  for (const r of rows) {
    const v = r[k]
    if (typeof v === 'number') {
      any = true
      total += v
    }
  }
  return any ? total : null
}

/**
 * The collapsed "Other paths" row. Additive figures are summed. Visitors are NOT — one person
 * on three staged pages would count three times — and neither is average time (a mean of
 * means); both read "—". The exit rate is recomputed from the summed exits and views.
 */
export const otherSummary = (other: readonly PageRowInput[]): PageRow => {
  const samplePageviews = sum(other, 'samplePageviews')
  const exits = sum(other, 'exits')
  return {
    path: '__other__',
    title: `Other paths (${other.length})`,
    published: false,
    visitors: null,
    pageviews: sum(other, 'pageviews'),
    leads: sum(other, 'leads'),
    samplePageviews,
    sampleVisitors: null,
    avgSeconds: null,
    timedViews: other.reduce((a, r) => a + r.timedViews, 0),
    entries: sum(other, 'entries'),
    exits,
    exitRate: exits !== null && samplePageviews ? Math.min(1, exits / samplePageviews) : null,
  }
}

// ── site flow ────────────────────────────────────────────────────────────────────

export interface FlowNodeInput {
  id: string
  column: number
  kind: 'page' | 'other' | 'exit' | 'more'
  path: string | null
  sessions: number
}

export interface FlowLinkInput {
  source: string
  target: string
  sessions: number
}

export const FLOW_NODE_LABELS = {
  other: 'Other pages',
} as const

/** A page as people say it: its published name, else the path. */
export const pageName = (path: string | null, titles: ReadonlyMap<string, string>): string =>
  path === null ? '—' : (titles.get(path) ?? otherTitle(path) ?? path)

/**
 * "Left after 1 page" … — the exit node's label in the chart. The shared SankeyChart (recharts)
 * draws every end node in the right-hand column, so an exit's label has to carry WHERE the
 * visit ended; the column it came from is no longer its position.
 */
export const exitLabel = (column: number): string => `Left after ${column} page${column === 1 ? '' : 's'}`

/**
 * API nodes/links → the shared SankeyChart's input (components/analytics/SankeyChart, VRG-691):
 * entry pages are `source` (one brand ramp step each, so neighbours are distinguishable), later
 * pages `stage`, "Other pages" and every exit `end` (grey, recessive), "More pages" `success`.
 */
export const flowChartData = (
  flows: { nodes: readonly FlowNodeInput[]; links: readonly FlowLinkInput[]; depth?: number },
  titles: ReadonlyMap<string, string>,
): {
  nodes: Array<{ id: string; label: string; tone: 'source' | 'stage' | 'end' | 'success' }>
  links: Array<{ source: string; target: string; value: number }>
} => ({
  nodes: flows.nodes.map((n) => ({
    id: n.id,
    label:
      n.kind === 'page'
        ? pageName(n.path, titles)
        : n.kind === 'exit'
          ? exitLabel(n.column)
          : n.kind === 'more'
            ? endLabel('more', flows.depth ?? n.column)
            : FLOW_NODE_LABELS.other,
    tone: n.kind === 'page' ? (n.column === 0 ? 'source' : 'stage') : n.kind === 'more' ? 'success' : 'end',
  })),
  links: flows.links.map((l) => ({ source: l.source, target: l.target, value: l.sessions })),
})

/** The outcome node's label, the same words the chart uses. */
export const endLabel = (end: 'exit' | 'more', pages: number): string =>
  end === 'exit' ? exitLabel(pages) : `Viewed more than ${pages} pages`

/** "Home → Pricing → Left after 2 pages" — the chart's own words, so the list and the chart agree. */
export const topFlowLabel = (
  flow: { steps: readonly string[]; end: 'exit' | 'more' },
  titles: ReadonlyMap<string, string>,
): string => [...flow.steps.map((s) => pageName(s, titles)), endLabel(flow.end, flow.steps.length)].join(' → ')
